import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Calendar, DollarSign, Activity, LogOut, Menu, X, MessageCircle, CalendarCheck, Settings, BarChart3, Target, Bell, Clock, AlertTriangle } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { PatientsManagement } from '../components/doctor/PatientsManagement';
import { AppointmentsManagement } from '../components/doctor/AppointmentsManagement';
import { CalendarAgenda } from '../components/doctor/CalendarAgenda';
import { FinancialManagement } from '../components/doctor/FinancialManagement';
import { EvolutionManagement } from '../components/doctor/EvolutionManagement';
import { NotificationsLog } from '../components/doctor/NotificationsLog';
import { CalendarEvents } from '../components/doctor/CalendarEvents';
import { IntegrationSettings } from '../components/doctor/IntegrationSettings';
import { AnalyticsDashboard } from '../components/doctor/AnalyticsDashboard';
import { GoalsDashboard } from '../components/doctor/GoalsDashboard';
import { AlertsReminders } from '../components/doctor/AlertsReminders';
import { DoctorAvailability } from '../components/doctor/DoctorAvailability';
import { DelinquencyReport } from '../components/doctor/DelinquencyReport';
import { DashboardHome } from '../components/doctor/DashboardHome';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const DoctorDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    fetchAlertCount();
  }, []);

  const fetchAlertCount = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts/count`, { withCredentials: true });
      setAlertCount(data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching alert count:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const navItems = [
    { path: '/doctor', label: 'Dashboard', icon: Activity },
    { path: '/doctor/patients', label: 'Pacientes', icon: Users },
    { path: '/doctor/appointments', label: 'Consultas', icon: Calendar },
    { path: '/doctor/evolution', label: 'Evolução', icon: Activity },
    { path: '/doctor/availability', label: 'Disponibilidade', icon: Clock },
    { path: '/doctor/alerts', label: 'Alertas', icon: Bell, badge: alertCount },
    { path: '/doctor/financial', label: 'Financeiro', icon: DollarSign },
    { path: '/doctor/delinquency', label: 'Inadimplência', icon: AlertTriangle },
    { path: '/doctor/analytics', label: 'Insights', icon: BarChart3 },
    { path: '/doctor/goals', label: 'Metas', icon: Target },
    { path: '/doctor/calendar', label: 'Calendário', icon: CalendarCheck },
    { path: '/doctor/notifications', label: 'WhatsApp', icon: MessageCircle },
    { path: '/doctor/settings', label: 'Configurações', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-[#0A1230]">
      {/* Header */}
      <header className="bg-white dark:bg-[#0F1B3D] border-b border-[#E5DCC8] dark:border-[#1A2952] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <img
                src="/images/logo-gp.png"
                alt="Garcia Passos Clinica Medica"
                className="h-10 sm:h-12"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-sm text-[#5F6B85] dark:text-[#FAF6EE]/70">{user?.name}</span>
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#E5DCC8] dark:border-[#1A2952] dark:text-[#FAF6EE]"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className={`md:w-64 ${mobileMenuOpen ? 'block' : 'hidden md:block'}`}>
            <nav className="space-y-2" data-testid="doctor-navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-[#0F1B3D] text-white'
                        : 'text-[#5F6B85] hover:bg-[#FAF6EE]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto bg-[#A8174E] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<DashboardHome />} />
              <Route path="/alerts" element={<AlertsReminders />} />
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="/goals" element={<GoalsDashboard />} />
              <Route path="/patients" element={<PatientsManagement />} />
              <Route path="/appointments" element={<CalendarAgenda />} />
              <Route path="/availability" element={<DoctorAvailability />} />
              <Route path="/evolution" element={<EvolutionManagement />} />
              <Route path="/financial" element={<FinancialManagement />} />
              <Route path="/delinquency" element={<DelinquencyReport />} />
              <Route path="/notifications" element={<NotificationsLog />} />
              <Route path="/calendar" element={<CalendarEvents />} />
              <Route path="/settings" element={<IntegrationSettings />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};
